/**
 * WB4.7 — medium/high/finance-tier state-change revision orchestration.
 */

import type { ObjectRef } from '@shared/business-workbench/workbench-focus';
import type { WorkbenchKernelExecutionSurface } from '@shared/business-workbench/action-types';
import type { WorkbenchActionExecutionEntry } from '@shared/business-workbench/workbench-action-execution-log';
import {
  advanceOperationalThreadStep,
  type OperationalThread,
} from '@shared/business-workbench/operational-thread';
import {
  financeReversalHandlerRef,
  isApprovalCorrectionHandlerRef,
  isBulkStateChangeHandlerRef,
  isFinanceCompensatingHandlerRef,
  isHighTierHandlerRef,
  resolveRecoverabilityTier,
} from '@shared/business-workbench/workbench-action-recoverability';
import {
  REVERSAL_STREAM_TEMPLATE_ID,
  REVERSAL_VOUCHER_STREAM_TYPE,
  resolveReversalCompensationRestoreAction,
  validateReversalCompensationInput,
} from '@shared/finance/workbench-reversal-compensation';
import type { WorkbenchTimelineEvent } from '@shared/business-workbench/workbench-timeline-event';
import {
  getLatestWorkbenchRevisionSnapshot,
  getWorkbenchRevisionSnapshotById,
  isWorkbenchRevisionSnapshotActive,
  markWorkbenchRevisionSnapshotRestored,
  saveWorkbenchRevisionSnapshot,
  type BulkStateChangeItemSnapshot,
  type WorkbenchRevisionRiskTier,
  type WorkbenchRevisionSnapshotPayload,
  type WorkbenchRevisionSnapshotRecord,
} from '@shared/business-workbench/workbench-revision-snapshot';
import { recordWorkbenchKernelAction } from '../ActionExecutor';
import { requestWorkbenchActionConfirmation } from '../workbench-high-tier-action-gate';
import {
  cancelFinanceReversalRemote,
  getFinanceReversalRemote,
  postFinanceReversalRemote,
  requestFinanceReversalRemote,
} from '../../../services/workbenchFinanceReversalApi';
import {
  fetchLatestWorkbenchRevisionSnapshotRemote,
  restoreWorkbenchRevisionSnapshotRemote,
  saveWorkbenchRevisionSnapshotRemote,
} from '../../../services/workbenchRevisionSnapshotApi';
import { restoreWorkbenchObjectRevisionRemote } from '../../../services/workbenchObjectRevisionApi';

export const WORKBENCH_REVISION_RESTORE_EVENT = 'inflomatrix:workbench-revision-restore';
export const WORKBENCH_ENTITY_RESTORE_EVENT = 'inflomatrix:workbench-entity-restore';
export const WORKBENCH_BULK_STATE_RESTORE_EVENT = 'inflomatrix:workbench-bulk-state-restore';
export const WORKBENCH_INLINE_FIELD_RESTORE_EVENT = 'inflomatrix:workbench-inline-field-restore';

export interface WorkbenchInlineFieldRestoreDetail {
  objectRef: ObjectRef;
  fieldKey: string;
  previousValue: string;
}

export function isMediumTierRevisionHandlerRef(handlerRef: string): boolean {
  return handlerRef.startsWith('thread:advance:');
}

export function isHighTierRevisionHandlerRef(handlerRef: string): boolean {
  return isHighTierHandlerRef(handlerRef);
}

export function isFinanceTierRevisionHandlerRef(handlerRef: string): boolean {
  return isFinanceCompensatingHandlerRef(handlerRef);
}

export function isBulkTierRevisionHandlerRef(handlerRef: string): boolean {
  return isBulkStateChangeHandlerRef(handlerRef);
}

export function isRevisionBackedHandlerRef(handlerRef: string): boolean {
  const tier = resolveRecoverabilityTier(handlerRef);
  return tier === 'medium' || tier === 'high' || tier === 'finance';
}

export async function recordRevisionBackedWorkbenchAction(input: {
  tenantId: string;
  objectRef: ObjectRef;
  action: { id: string; label: string; handlerRef: string };
  payload: WorkbenchRevisionSnapshotPayload;
  riskTier?: WorkbenchRevisionRiskTier;
  undoable?: boolean;
  surface?: WorkbenchKernelExecutionSurface;
  timelineChange?: WorkbenchTimelineEvent['change'];
}): Promise<{
  snapshot: WorkbenchRevisionSnapshotRecord | null;
  entry: WorkbenchActionExecutionEntry;
}> {
  const recoverabilityTier = resolveRecoverabilityTier(input.action.handlerRef);
  const usesApprovalCorrection = isApprovalCorrectionHandlerRef(input.action.handlerRef);
  const snapshot = await persistWorkbenchRevisionSnapshot({
    tenantId: input.tenantId,
    objectRef: input.objectRef,
    actionId: input.action.id,
    actionLabel: input.action.label,
    handlerRef: input.action.handlerRef,
    payload: input.payload,
    riskTier:
      input.riskTier ??
      (recoverabilityTier === 'finance'
        ? 'finance'
        : recoverabilityTier === 'high'
          ? 'high'
          : 'medium'),
  });

  const entry = recordWorkbenchKernelAction({
    tenantId: input.tenantId,
    objectRef: input.objectRef,
    action: input.action,
    undoable: input.undoable ?? (usesApprovalCorrection ? false : Boolean(snapshot)),
    ...(snapshot
      ? {
          revisionSnapshotId: snapshot.id,
          revisionSnapshotPayload: snapshot.payload,
        }
      : {}),
    ...(recoverabilityTier ? { recoverabilityTier } : {}),
    ...(input.surface ? { surface: input.surface } : {}),
    ...(input.timelineChange ? { timelineChange: input.timelineChange } : {}),
  });

  return { snapshot, entry };
}

export async function recordEntityDeleteWithRevision(input: {
  tenantId: string;
  objectRef: ObjectRef;
  entitySnapshot: unknown;
  actionLabel?: string;
  surface?: WorkbenchKernelExecutionSurface;
}): Promise<{
  snapshot: WorkbenchRevisionSnapshotRecord | null;
  entry: WorkbenchActionExecutionEntry;
}> {
  return recordRevisionBackedWorkbenchAction({
    tenantId: input.tenantId,
    objectRef: input.objectRef,
    action: {
      id: 'delete',
      label: input.actionLabel ?? '삭제',
      handlerRef: 'crud:delete',
    },
    payload: {
      kind: 'entity_delete_before',
      entitySnapshot: input.entitySnapshot,
    },
    riskTier: 'high',
    ...(input.surface ? { surface: input.surface } : {}),
  });
}

export async function recordFlowStartWithRevision(input: {
  tenantId: string;
  objectRef: ObjectRef;
  flowTemplateId: string;
  actionLabel: string;
  context?: unknown;
  surface?: WorkbenchKernelExecutionSurface;
}): Promise<{
  snapshot: WorkbenchRevisionSnapshotRecord | null;
  entry: WorkbenchActionExecutionEntry;
}> {
  const handlerRef = `flow:start:${input.flowTemplateId}`;
  return recordRevisionBackedWorkbenchAction({
    tenantId: input.tenantId,
    objectRef: input.objectRef,
    action: {
      id: `start_flow:${input.flowTemplateId}`,
      label: input.actionLabel,
      handlerRef,
    },
    payload: {
      kind: 'flow_start_context',
      flowTemplateId: input.flowTemplateId,
      ...(input.context !== undefined ? { context: input.context } : {}),
    },
    riskTier: 'high',
    ...(input.surface ? { surface: input.surface } : {}),
  });
}

export async function recordFinanceReversalRequest(input: {
  tenantId: string;
  objectRef: ObjectRef;
  sourceVoucherId: string;
  actionLabel?: string;
  streamId?: string;
  surface?: WorkbenchKernelExecutionSurface;
}): Promise<{
  snapshot: WorkbenchRevisionSnapshotRecord | null;
  entry: WorkbenchActionExecutionEntry;
  reversalRequestId: string;
  requiresApproval: boolean;
}> {
  const validation = validateReversalCompensationInput({
    sourceVoucherId: input.sourceVoucherId,
    streamType: REVERSAL_VOUCHER_STREAM_TYPE,
    ...(input.streamId ? { streamId: input.streamId } : {}),
  });
  if (!validation.valid) {
    throw new Error(validation.errors.join('; '));
  }

  const handlerRef = financeReversalHandlerRef(input.sourceVoucherId);
  const reversalRequest = await requestFinanceReversalRemote({
    tenantId: input.tenantId,
    sourceVoucherId: input.sourceVoucherId,
    objectId: input.objectRef.id,
    businessDomain: input.objectRef.businessDomain,
    ...(input.streamId ? { streamId: input.streamId } : {}),
  });
  if (!reversalRequest) {
    throw new Error(
      'finance reversal request failed: remote API unavailable or returned no request record'
    );
  }

  const result = await recordRevisionBackedWorkbenchAction({
    tenantId: input.tenantId,
    objectRef: input.objectRef,
    action: {
      id: `finance_reversal:${input.sourceVoucherId}`,
      label: input.actionLabel ?? '역분개 요청',
      handlerRef,
    },
    payload: {
      kind: 'finance_reversal_pending',
      sourceVoucherId: input.sourceVoucherId,
      streamType: REVERSAL_VOUCHER_STREAM_TYPE,
      streamTemplateId: REVERSAL_STREAM_TEMPLATE_ID,
      requiresApproval: reversalRequest.requiresApproval,
      reversalRequestId: reversalRequest.id,
      reversalRequestStatus: reversalRequest.status,
      ...(reversalRequest.compensatingStreamId
        ? { compensatingStreamId: reversalRequest.compensatingStreamId }
        : {}),
      ...(reversalRequest.compensatingVoucherId
        ? { compensatingVoucherId: reversalRequest.compensatingVoucherId }
        : {}),
    },
    riskTier: 'finance',
    ...(input.surface ? { surface: input.surface } : {}),
  });

  return {
    ...result,
    reversalRequestId: reversalRequest.id,
    requiresApproval: reversalRequest.requiresApproval,
  };
}

export async function postFinanceReversalAfterApproval(input: {
  tenantId: string;
  requestId: string;
  compensatingVoucherId?: string;
}): Promise<NonNullable<Awaited<ReturnType<typeof postFinanceReversalRemote>>>> {
  const postedRequest = await postFinanceReversalRemote({
    tenantId: input.tenantId,
    requestId: input.requestId,
    ...(input.compensatingVoucherId ? { compensatingVoucherId: input.compensatingVoucherId } : {}),
  });
  if (!postedRequest) {
    throw new Error(
      'finance reversal post failed: remote API unavailable or returned no request record'
    );
  }
  return postedRequest;
}

/** @deprecated Prefer recordFinanceReversalRequest + postFinanceReversalAfterApproval (Wave 1 FSM split). */
export async function recordFinanceReversalWithRevision(input: {
  tenantId: string;
  objectRef: ObjectRef;
  sourceVoucherId: string;
  actionLabel?: string;
  streamId?: string;
  surface?: WorkbenchKernelExecutionSurface;
}): Promise<{
  snapshot: WorkbenchRevisionSnapshotRecord | null;
  entry: WorkbenchActionExecutionEntry;
  reversalRequestId?: string;
}> {
  const { snapshot, entry, reversalRequestId, requiresApproval } =
    await recordFinanceReversalRequest(input);

  if (!requiresApproval) {
    await postFinanceReversalAfterApproval({
      tenantId: input.tenantId,
      requestId: reversalRequestId,
    });
  }

  return { snapshot, entry, reversalRequestId };
}

export async function executeGatedFinanceReversalAction(input: {
  sourceVoucherId: string;
  actionLabel: string;
  onConfirmed: () => void | Promise<void>;
}): Promise<boolean> {
  return executeGatedHighTierWorkbenchAction({
    handlerRef: financeReversalHandlerRef(input.sourceVoucherId),
    actionLabel: input.actionLabel,
    onConfirmed: input.onConfirmed,
  });
}

export async function executeGatedHighTierWorkbenchAction(input: {
  handlerRef: string;
  actionLabel: string;
  onConfirmed: () => void | Promise<void>;
}): Promise<boolean> {
  const confirmed = await requestWorkbenchActionConfirmation({
    handlerRef: input.handlerRef,
    actionLabel: input.actionLabel,
  });
  if (!confirmed) {
    return false;
  }
  await input.onConfirmed();
  return true;
}

export async function persistWorkbenchRevisionSnapshot(
  input: Parameters<typeof saveWorkbenchRevisionSnapshot>[0]
): Promise<WorkbenchRevisionSnapshotRecord | null> {
  const remote = await saveWorkbenchRevisionSnapshotRemote(input);
  if (remote) {
    return remote;
  }
  return saveWorkbenchRevisionSnapshot(input);
}

export async function resolveActiveWorkbenchRevisionSnapshot(input: {
  tenantId: string;
  objectId: string;
}): Promise<WorkbenchRevisionSnapshotRecord | null> {
  const remote = await fetchLatestWorkbenchRevisionSnapshotRemote(input);
  if (remote) {
    return remote;
  }
  return getLatestWorkbenchRevisionSnapshot(input);
}

export async function recordThreadStepAdvanceWithRevision(input: {
  tenantId: string;
  thread: OperationalThread;
  stepId: string;
  stepLabel: string;
}): Promise<{
  nextThread: OperationalThread;
  snapshot: WorkbenchRevisionSnapshotRecord | null;
  entry: WorkbenchActionExecutionEntry | null;
}> {
  const nextThread = advanceOperationalThreadStep(input.thread, input.stepId);
  if (nextThread === input.thread) {
    return { nextThread: input.thread, snapshot: null, entry: null };
  }

  const objectRef: ObjectRef = input.thread.rootObject;
  const handlerRef = `thread:advance:${input.stepId}`;
  const snapshot = await persistWorkbenchRevisionSnapshot({
    tenantId: input.tenantId,
    objectRef,
    actionId: `thread_advance:${input.stepId}`,
    actionLabel: `단계 진행: ${input.stepLabel}`,
    handlerRef,
    payload: {
      kind: 'operational_thread_advance',
      stepId: input.stepId,
      beforeThread: input.thread,
    },
  });

  const entry = recordWorkbenchKernelAction({
    tenantId: input.tenantId,
    objectRef,
    action: {
      id: `thread_advance:${input.stepId}`,
      label: `단계 진행: ${input.stepLabel}`,
      handlerRef,
    },
    undoable: Boolean(snapshot),
    ...(snapshot
      ? {
          revisionSnapshotId: snapshot.id,
          revisionSnapshotPayload: snapshot.payload,
          recoverabilityTier: 'medium' as const,
        }
      : {}),
  });

  return { nextThread, snapshot, entry };
}

export async function recordBulkStateChangeWithRevision(input: {
  tenantId: string;
  anchorObjectRef: ObjectRef;
  action: { id: string; label: string; handlerRef: string };
  beforeStates: BulkStateChangeItemSnapshot[];
  surface?: WorkbenchKernelExecutionSurface;
}): Promise<{
  snapshot: WorkbenchRevisionSnapshotRecord | null;
  entry: WorkbenchActionExecutionEntry;
}> {
  const recoverabilityTier = resolveRecoverabilityTier(input.action.handlerRef);
  const riskTier: WorkbenchRevisionRiskTier = recoverabilityTier === 'high' ? 'high' : 'medium';

  return recordRevisionBackedWorkbenchAction({
    tenantId: input.tenantId,
    objectRef: input.anchorObjectRef,
    action: input.action,
    payload: {
      kind: 'bulk_state_change_before',
      handlerRef: input.action.handlerRef,
      selectionCount: input.beforeStates.length,
      beforeStates: input.beforeStates,
    },
    riskTier,
    ...(input.surface ? { surface: input.surface } : {}),
  });
}

export async function restoreWorkbenchRevisionSnapshot(input: {
  tenantId: string;
  snapshotId: string;
  objectRef: ObjectRef;
}): Promise<{
  restored: boolean;
  payload?: WorkbenchRevisionSnapshotRecord['payload'];
  entry?: WorkbenchActionExecutionEntry;
}> {
  let restoredSnapshot =
    (await restoreWorkbenchObjectRevisionRemote({
      tenantId: input.tenantId,
      revisionId: input.snapshotId,
    })) ??
    (await restoreWorkbenchRevisionSnapshotRemote({
      tenantId: input.tenantId,
      snapshotId: input.snapshotId,
    }));

  if (!restoredSnapshot) {
    const localSnapshot = getWorkbenchRevisionSnapshotById(input.snapshotId);
    if (
      localSnapshot &&
      localSnapshot.tenantId === input.tenantId &&
      isWorkbenchRevisionSnapshotActive(localSnapshot)
    ) {
      restoredSnapshot = markWorkbenchRevisionSnapshotRestored(input.snapshotId);
    }
  }

  if (!restoredSnapshot) {
    return { restored: false };
  }

  const remote = restoredSnapshot;

  if (remote.payload.kind === 'finance_reversal_pending' && remote.payload.reversalRequestId) {
    const snapshotStatus = remote.payload.reversalRequestStatus;
    const restoreAction = resolveReversalCompensationRestoreAction(snapshotStatus ?? 'pending', {
      ...(remote.payload.compensatingStreamId
        ? { compensatingStreamId: remote.payload.compensatingStreamId }
        : {}),
    });

    if (restoreAction === 'cancel_pending') {
      const liveRequest = await getFinanceReversalRemote({
        tenantId: input.tenantId,
        requestId: remote.payload.reversalRequestId,
      });
      const liveStatus = liveRequest?.status ?? snapshotStatus ?? 'pending';
      if (
        resolveReversalCompensationRestoreAction(liveStatus, {
          ...((liveRequest?.compensatingStreamId ?? remote.payload.compensatingStreamId)
            ? {
                compensatingStreamId:
                  liveRequest?.compensatingStreamId ?? remote.payload.compensatingStreamId,
              }
            : {}),
        }) === 'cancel_pending'
      ) {
        await cancelFinanceReversalRemote({
          tenantId: input.tenantId,
          requestId: remote.payload.reversalRequestId,
        });
      }
    }
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(WORKBENCH_REVISION_RESTORE_EVENT, {
        detail: { snapshot: remote },
      })
    );

    if (remote.payload.kind === 'entity_delete_before') {
      window.dispatchEvent(
        new CustomEvent(WORKBENCH_ENTITY_RESTORE_EVENT, {
          detail: {
            entitySnapshot: remote.payload.entitySnapshot,
            objectRef: input.objectRef,
          },
        })
      );
    }

    if (remote.payload.kind === 'bulk_state_change_before') {
      window.dispatchEvent(
        new CustomEvent(WORKBENCH_BULK_STATE_RESTORE_EVENT, {
          detail: {
            beforeStates: remote.payload.beforeStates,
            handlerRef: remote.payload.handlerRef,
            objectRef: input.objectRef,
          },
        })
      );
    }

    if (remote.payload.kind === 'inline_field_change_before') {
      window.dispatchEvent(
        new CustomEvent<WorkbenchInlineFieldRestoreDetail>(WORKBENCH_INLINE_FIELD_RESTORE_EVENT, {
          detail: {
            objectRef: input.objectRef,
            fieldKey: remote.payload.fieldKey,
            previousValue: remote.payload.previousValue,
          },
        })
      );
    }
  }

  const restoreLabel =
    remote.payload.kind === 'finance_reversal_pending'
      ? remote.payload.reversalRequestStatus === 'posted'
        ? '역분개 기록 복원 (전기 유지)'
        : '역분개 보상 복원'
      : remote.payload.kind === 'bulk_state_change_before'
        ? '일괄 변경 복원'
        : '이전 상태로 복원';

  const entry = recordWorkbenchKernelAction({
    tenantId: input.tenantId,
    objectRef: input.objectRef,
    action: {
      id: `revision_restore:${remote.id}`,
      label: restoreLabel,
      handlerRef: `revision:restore:${remote.id}`,
    },
    undoable: false,
    ...(remote.riskTier === 'finance' ? { recoverabilityTier: 'finance' as const } : {}),
    ...(remote.riskTier === 'high' ? { recoverabilityTier: 'high' as const } : {}),
  });

  return { restored: true, payload: remote.payload, entry };
}

export function applyOperationalThreadRevisionPayload(
  payload: WorkbenchRevisionSnapshotRecord['payload']
): OperationalThread | null {
  if (payload.kind !== 'operational_thread_advance') {
    return null;
  }
  return payload.beforeThread as OperationalThread;
}

export function applyBulkStateChangeRevisionPayload(
  payload: WorkbenchRevisionSnapshotRecord['payload']
): BulkStateChangeItemSnapshot[] | null {
  if (payload.kind !== 'bulk_state_change_before') {
    return null;
  }
  return payload.beforeStates;
}
